import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { renameChatSession } from '../utils/api';
import '../layouts/SessionsLayout.css';

function sessionDisplayName(name) {
  const trimmed = (name || '').trim();
  return trimmed || 'Untitled Session';
}

const SessionListItem = ({ session, projectId, isActive, onRenamed, variant = 'sidebar' }) => {
  const isNav = variant === 'nav';
  const ownerEmail = session.owner?.email || null;
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const beginRename = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraft((session.name || '').trim());
    setRenaming(true);
    setError('');
  };

  const cancelRename = () => {
    setRenaming(false);
    setDraft('');
    setError('');
  };

  const saveRename = async (e) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name) {
      setError('Name cannot be empty');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await renameChatSession(session.session_id, name);
      onRenamed?.(session.session_id, name);
      try {
        window.dispatchEvent(new CustomEvent('cascade:session-renamed', {
          detail: {
            session_id: session.session_id,
            name,
            project_id: projectId,
          },
        }));
      } catch (_) { /* no-op */ }
      setRenaming(false);
    } catch (err) {
      setError(err.message || 'Failed to rename chat');
    } finally {
      setSaving(false);
    }
  };

  const renameForm = (
    <form className="session-rename-form" onSubmit={saveRename}>
      <input
        type="text"
        className="input session-rename-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') cancelRename();
        }}
        placeholder="Chat name"
        maxLength={200}
        autoFocus
        disabled={saving}
      />
      <div className="session-rename-actions">
        <button type="submit" className="btn btn-sm primary" disabled={saving || !draft.trim()}>
          {saving ? <i className="fas fa-spinner fa-pulse" /> : 'Save'}
        </button>
        <button type="button" className="btn btn-sm" onClick={cancelRename} disabled={saving}>
          Cancel
        </button>
      </div>
      {error && <div className="session-rename-error">{error}</div>}
    </form>
  );

  if (renaming) {
    if (isNav) {
      return (
        <li className={`session-nav-item ${isActive ? 'active' : ''} session-item--renaming`}>
          {renameForm}
        </li>
      );
    }
    return (
      <div className={`session-item ${isActive ? 'active' : ''} session-item--renaming`}>
        {renameForm}
      </div>
    );
  }

  if (isNav) {
    return (
      <li className={`session-nav-item ${isActive ? 'active' : ''}`}>
        <NavLink
          to={`/p/${projectId}/c/${session.session_id}`}
          className={({ isActive: linkActive }) =>
            `session-nav-link ${linkActive || isActive ? 'active' : ''}`
          }
          title={sessionDisplayName(session.name)}
        >
          <i className="fas fa-comments" aria-hidden="true" />
          <span className="session-nav-name">{sessionDisplayName(session.name)}</span>
          <button
            type="button"
            className="session-rename-btn session-rename-btn--nav"
            title="Rename chat"
            aria-label={`Rename ${sessionDisplayName(session.name)}`}
            onClick={beginRename}
          >
            <i className="fas fa-comment-dots" aria-hidden="true" />
          </button>
          {ownerEmail && (
            <span className="session-nav-owner" title={`Owner: ${ownerEmail}`}>
              <i className="fas fa-user" aria-hidden="true" />
              {ownerEmail.split('@')[0]}
            </span>
          )}
        </NavLink>
      </li>
    );
  }

  return (
    <div className={`session-item ${isActive ? 'active' : ''}`}>
      <NavLink
        to={`/p/${projectId}/c/${session.session_id}`}
        className={({ isActive: linkActive }) =>
          `session-item-main ${linkActive || isActive ? 'active' : ''}`
        }
      >
        <i className="fas fa-message" aria-hidden="true" />
        <div className="session-item-text">
          <div className="session-item-title-row">
            <span className="session-item-name">{sessionDisplayName(session.name)}</span>
            <button
              type="button"
              className="session-rename-btn"
              title="Rename chat"
              aria-label={`Rename ${sessionDisplayName(session.name)}`}
              onClick={beginRename}
            >
              <i className="fas fa-comment-dots" aria-hidden="true" />
            </button>
          </div>
          <div className="session-item-date">
            {new Date(session.modified_at || session.created_at).toLocaleString()}
            {session.archived_at && (
              <span className="session-archived-badge" title="Archived">
                <i className="fas fa-box-archive" aria-hidden="true" />
              </span>
            )}
          </div>
        </div>
      </NavLink>
    </div>
  );
};

export default SessionListItem;
